// The user's Procore connection: connected as <email> / not connected, with Connect
// (a token from the browser, the browser connector, or the Procore sign-in - see
// ProcoreConnectModal) and Disconnect - the AccConnectionPanel shape. `compact` is the
// personal-area strip; the profile tab uses the full panel.
import React, { useEffect } from "react";
import { Modal, Spin } from "antd";
import addNotification from "helpers/notify";
import { disconnectProcore, getProcoreStatus } from "./integrationsApi";
import { useProviderStatus, refreshProviderStatus } from "./useIntegrations";
import { ProcoreLogo } from "./ProviderLogos";
import { openProcoreConnect } from "./ProcoreConnectModal";
import AccountsCount from "./AccountsCount";
import "components/Acc/acc.scss";

/** Open the Procore connect window (token, browser connector, or sign-in). */
export async function connectProcore() {
  let status = null;
  try {
    status = await getProcoreStatus();
  } catch (e) {
    status = null;
  }
  openProcoreConnect(status);
}

export const PROCORE_METHOD = {
  token: "with a token from the browser",
  browser: "with the browser session",
  oauth: "with Procore sign-in",
};

export default function ProcoreConnectionPanel({ compact = false }) {
  const status = useProviderStatus("procore");

  // back from the Procore sign-in: /personal-area?procore=connected|cancelled|expired
  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("procore");
    if (!q) return;
    if (q === "connected") addNotification("Procore account connected", "success");
    else if (q === "cancelled") addNotification("Procore sign-in was cancelled", "warning");
    else if (q === "expired") addNotification("The Procore sign-in expired - connect again", "warning");
    const u = new URL(window.location.href);
    u.searchParams.delete("procore");
    window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    refreshProviderStatus("procore");
  }, []);

  const disconnect = () =>
    Modal.confirm({
      title: "Disconnect the Procore account?",
      content: "Projects keep their Procore folders, but nothing is delivered with your account until it is connected again.",
      okText: "Disconnect",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await disconnectProcore();
          addNotification("Procore account disconnected", "success");
        } catch (e) {
          addNotification(e.message, "danger");
        }
        refreshProviderStatus("procore");
      },
    });

  if (!status) return compact ? null : <Spin />;
  // NOT FOR THIS READER - same rule as the ACC panel (owner 2026-10-19):
  // a project owner, a company admin or an admin, because they set the
  // automatic folder delivery. /procore/status answers `allowed: false`.
  if (status.allowed === false) {
    return compact ? null : <p className="text-muted">{status.reason || "Connecting Procore is limited to a project owner, a company admin, an admin, or someone granted the procoreConnect permission."}</p>;
  }
  // the first account drives Connect / Disconnect; the state says whether ANY of the
  // company's Procore accounts works, and the count lists them all (owner 2026-10-25)
  const on = !!status.connected;
  const anyOn = on || (status.accounts || []).some((a) => a.connected);
  const how = on
    ? `${PROCORE_METHOD[status.method] || ""}${status.method === "browser" && status.source === "extension" ? " (connector)" : ""}`
    : "";

  const body = (
    <div className={`acc-connection ${compact ? "compact" : ""}`}>
      <ProcoreLogo connected={anyOn} size={compact ? 20 : 28} />
      <div className="acc-connection-text">
        <div>
          Procore: <span className={`acc-state ${anyOn ? "on" : "off"}`}>{anyOn ? "Connected" : "Not connected"}</span>
          {" · "}
          <AccountsCount list={status.accounts} name="Procore" testId="header-accounts-procore" />
        </div>
        {on ? <small className="text-muted">{status.email ? `as ${status.email} ` : ""}{how}</small> : null}
        {!on && status.error ? <small className="text-danger d-block">{status.error}</small> : null}
      </div>
      {on ? (
        <span className="d-inline-flex" style={{ gap: 6 }}>
          {status.method === "token" || (status.method === "browser" && status.source !== "extension") ? (
            <button className="bg-white text-bid border-bid shadow-none px-3" title="Paste a fresh token, or switch to the browser connector" onClick={connectProcore}>
              Update
            </button>
          ) : null}
          <button className="bg-white text-bid border-bid shadow-none px-3" onClick={disconnect}>
            Disconnect
          </button>
        </span>
      ) : (
        <button className="header-btn text-white bg-bid border-bid px-3" onClick={connectProcore}>
          Connect Procore
        </button>
      )}
    </div>
  );

  if (compact) return body;
  return (
    <div className="w-100">
      <div className="py-4 px-2">
        <p className="font-weight-bold text-black mb-1 h3">Procore</p>
        <p>
          Connect your Procore account to deliver a project&apos;s files to Procore Documents: approved shop drawings, and the
          folders chosen for drawings, the Revit model and the Navisworks model (the settings icon beside the Procore logo on
          each project card). Connect with a token copied from your browser, with the BidLight Procore Connector browser
          extension, or with the Procore sign-in where it is set up.
        </p>
        <hr />
        <div className="plan-container mt-4">
          <div className="plan-content">{body}</div>
        </div>
      </div>
    </div>
  );
}
